import { useCallback, useContext, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { Loader } from '../Common/Loader';
import { useTranslate } from '@/hooks/useTranslate';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { useSelectedPaymentMethod } from '@/hooks/useSelectedPaymentMethod';

interface PayButtonProps {
  price: string;
  onClick?: () => void;
  isDisabled?: boolean;
}

export const PayButton = ({ price, onClick, isDisabled }: PayButtonProps) => {
  const context = useContext(CheckoutContext);
  const {
    payment: { isInPaymentProcess },
    actions: { runPayment },
  } = context;

  const { t } = useTranslate();

  const isButtonDisabled = useMemo(() => {
    return isDisabled || isInPaymentProcess;
  }, [isDisabled, isInPaymentProcess]);

  const { selectedPaymentMethod } = useSelectedPaymentMethod();

  const handleClick = useCallback(() => {
    if (!isButtonDisabled) {
      trackAndNotifyPaymentClick(context, selectedPaymentMethod);
      if (onClick) {
        onClick();
      } else {
        runPayment();
      }
    }
  }, [context, isButtonDisabled, onClick, selectedPaymentMethod, runPayment]);
  return (
    <div
      data-testid="pay-button"
      className={`w-full bg-blue h-11 rounded-md flex items-center justify-center p-spacing-2 gap-2 cursor-pointer ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={handleClick}
    >
      {isInPaymentProcess ? (
        <Loader size="xs" color="white" />
      ) : (
        <div data-testid="pay-button-text" className={`text-white font-medium text-sm md:cursor-pointer`}>
          {t('pay') + ' ' + price}
        </div>
      )}
    </div>
  );
};
